import { useMutation } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLoginNudgeStore } from '@/store/login-nudge-store'
import type { User } from '@/types'
import { deleteCookie, HASHED_EMAIL_COOKIE, updateHashedEmailCookie } from '@/utils/cookie-parser'
import { sha256hex } from '@/utils/crypto'
import { useAPI } from '@/utils/requests/api-plugin'

import { useLayoutStore } from '../layout-store'

export const useUserStore = defineStore('user', () => {
    const user = ref<User | null>(null)
    const api = useAPI()
    const userLoadingPromise = ref<Promise<User | null> | null>(null)
    const hasAuthToken = ref(true)
    const isIdentityResolved = ref(false)
    const _isUserInfoLoaded = ref(false)
    const isAuthorized = computed(() => !!user.value)

    const isCompanyEmployeeLoggedIn = computed(() => {
        return !!user.value?.email?.endsWith('@playgama.com')
    })

    const layoutStore = useLayoutStore()
    const loginNudgeStore = useLoginNudgeStore()
    function fetchUserInfo(): Promise<User | null> {
        _isUserInfoLoaded.value = false

        userLoadingPromise.value = Promise.resolve().then(async () => {
            if (import.meta.env.SSR) {
                _isUserInfoLoaded.value = true
                return null
            }

            await updateUserSilently()
            _isUserInfoLoaded.value = true

            if (user.value) {
                loginNudgeStore.reset()
            }

            return user.value
        })

        return userLoadingPromise.value
    }

    function publishUser(next: User | null) {
        user.value = next
        // getUser answers from this promise, so it has to move with the user it reports. A stale
        // one sends an authorized player's saves to the guest store.
        userLoadingPromise.value = Promise.resolve(next)
    }

    // silent polling without UI interaction
    async function updateUserSilently() {
        const result = await api.getCurrentUser()

        if (result.status !== 'ok') {
            return
        }

        publishUser(result.data)
        isIdentityResolved.value = true

        if (user.value?.email) {
            const normalizedEmail = user.value.email.replaceAll(/\.\+/g, '')
            sha256hex(normalizedEmail).then(updateHashedEmailCookie)
        } else {
            deleteCookie(HASHED_EMAIL_COOKIE)
        }
    }

    function getUser(): Promise<User | null> {
        if (userLoadingPromise.value) {
            return userLoadingPromise.value
        }

        if (import.meta.env.SSR === false && hasAuthToken.value) {
            userLoadingPromise.value = fetchUserInfo()
        } else {
            userLoadingPromise.value = Promise.resolve(null)
            _isUserInfoLoaded.value = true
            isIdentityResolved.value = import.meta.env.SSR === false
        }

        return userLoadingPromise.value
    }

    /**
     * Whether the player's saves belong to this frame rather than to an account.
     *
     * A visitor with no auth cookie never had a session, so that is answered without a request.
     * Otherwise only a lookup that came back *without* a user counts as a guest: a lookup that
     * failed leaves the session unknown, and calling that a guest would put a logged-in player's
     * progress in frame storage, where a later session would never look for it. The server
     * authenticates the request anyway, so an unknown session is left to it.
     */
    async function isGuest(): Promise<boolean> {
        if (!hasAuthToken.value) {
            return true
        }

        if (await getUser()) {
            return false
        }

        return isIdentityResolved.value
    }

    async function authorizeUser(source: string): ReturnType<typeof getUser> {
        await getUser()
        if (!user.value) {
            await new Promise<void>((resolve) => {
                layoutStore.openSignInModal(source, { auto: true })
                layoutStore.$subscribe((mutation, state) => {
                    if (mutation.type === 'direct' && !state.signInModalOpened) {
                        fetchUserInfo().then(() => resolve())
                    }
                })
            })
        }

        if (user.value) {
            return { ...user.value }
        }

        throw new Error("User didn't complete login")
    }

    async function logout() {
        userLoadingPromise.value = Promise.resolve(null)
        isIdentityResolved.value = true
        await api.logout()
        publishUser(null)
        deleteCookie(HASHED_EMAIL_COOKIE)
    }

    async function blockAccount() {
        const result = await api.blockAccount()
        if (result.status !== 'ok') {
            return false
        }

        publishUser(null)
        deleteCookie(HASHED_EMAIL_COOKIE)
        return true
    }

    function cancelCharge(reason: string) {
        layoutStore.paymentModalOrder?.reject?.(reason)

        layoutStore.setPaymentModalOrder(null)
    }

    const useMutationUsersGame = useMutation({
        mutationKey: ['user', 'my-games', 'update'],
        mutationFn: (hru: string) => {
            return api.request('/api/v1/user/my-games', {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ hru }),
            })
        },
        onError: () => {
            console.error('Failed to update users game')
        },
    })
    function updateUsersGame(hru: string) {
        if (!user.value) {
            return
        }
        useMutationUsersGame.mutateAsync(hru)
    }

    function setUser(data: User | null) {
        publishUser(data)
        _isUserInfoLoaded.value = true
        isIdentityResolved.value = true
    }

    return {
        user,
        isAuthorized,
        isCompanyEmployeeLoggedIn,
        hasAuthToken,
        /** @deprecated use-user-state.ts */
        _isUserInfoLoaded,
        getUser,
        isGuest,
        logout,
        blockAccount,
        authorizeUser,
        fetchUserInfo,
        updateUserSilently,
        cancelCharge,
        updateUsersGame,
        chargeUser: layoutStore.setPaymentModalOrder,
        setUser,
    }
})
